<script setup lang="ts">
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRoot,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'reka-ui'

import EIcon from './EIcon.vue'
import type { MenuItem } from './types'

/** The "More…" menu on every document action bar and every table row. */

withDefaults(
  defineProps<{
    items: readonly MenuItem[]
    align?: 'start' | 'center' | 'end'
    side?: 'top' | 'right' | 'bottom' | 'left'
    label?: string
  }>(),
  { align: 'end', side: 'bottom', label: '' },
)

const emit = defineEmits<{ select: [item: MenuItem] }>()

const open = defineModel<boolean>('open', { default: false })

function groupChanged(items: readonly MenuItem[], index: number): boolean {
  const current = items[index]?.group
  if (current === undefined) return false
  return index === 0 || items[index - 1]?.group !== current
}
</script>

<template>
  <DropdownMenuRoot v-model:open="open">
    <DropdownMenuTrigger as-child>
      <slot name="trigger" />
    </DropdownMenuTrigger>

    <DropdownMenuPortal>
      <DropdownMenuContent
        class="e-popover e-menu"
        :align="align"
        :side="side"
        :side-offset="4"
        :aria-label="label === '' ? undefined : label"
      >
        <template v-for="(item, index) in items" :key="item.id">
          <DropdownMenuSeparator v-if="index > 0 && groupChanged(items, index)" class="e-menu__separator" />
          <DropdownMenuLabel v-if="groupChanged(items, index)" class="e-menu__group">
            {{ item.group }}
          </DropdownMenuLabel>

          <DropdownMenuItem
            class="e-menu__item"
            :class="{ 'e-menu__item--destructive': item.destructive === true }"
            :disabled="item.disabled ?? false"
            @select="emit('select', item)"
          >
            <EIcon v-if="item.icon" :name="item.icon" :size="14" />
            <span class="e-menu__label">{{ item.label }}</span>
            <span v-if="item.shortcut" class="e-menu__shortcut">{{ item.shortcut }}</span>
          </DropdownMenuItem>
        </template>
      </DropdownMenuContent>
    </DropdownMenuPortal>
  </DropdownMenuRoot>
</template>

<style>
.e-menu {
  z-index: var(--erpx-z-dropdown);
  min-width: 11rem;
  padding: var(--erpx-space-1);
}

.e-menu__item {
  display: flex;
  align-items: center;
  gap: var(--erpx-space-2);
  min-height: var(--erpx-row-h-compact);
  padding-inline: var(--erpx-space-2);
  border-radius: var(--erpx-radius-sm);
  color: var(--erpx-color-text-primary);
  font-size: var(--erpx-text-sm);
  cursor: pointer;
  user-select: none;
  outline: none;
}

.e-menu__item[data-highlighted] {
  background-color: var(--erpx-color-surface-hover);
}

.e-menu__item[data-disabled] {
  color: var(--erpx-color-text-disabled);
  pointer-events: none;
}

.e-menu__item--destructive {
  color: var(--erpx-color-danger-text);
}

.e-menu__item--destructive[data-highlighted] {
  background-color: var(--erpx-color-danger-surface);
}

.e-menu__label {
  flex: 1;
  min-width: 0;
}

.e-menu__shortcut {
  color: var(--erpx-color-text-muted);
  font-family: var(--erpx-font-mono);
  font-size: var(--erpx-text-2xs);
}

.e-menu__separator {
  height: 1px;
  margin-block: var(--erpx-space-1);
  background-color: var(--erpx-color-border-subtle);
}

.e-menu__group {
  padding: var(--erpx-space-2) var(--erpx-space-2) var(--erpx-space-1);
  color: var(--erpx-color-text-muted);
  font-size: var(--erpx-text-2xs);
  font-weight: var(--erpx-weight-semibold);
  letter-spacing: var(--erpx-tracking-wide);
  text-transform: uppercase;
}
</style>
